import { inject } from '@angular/core';
import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService, SKIP_AUTH } from '../services/auth.service';
import { decodeJwt } from '../../shared/helpers/jwt';

function addAuthorizationHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${token}`)
  });
}

export function tokenInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);

  if (request.context.get(SKIP_AUTH)) return next(request);

  const authData = authService.authData;
  if (!authData) {
    console.error('No auth data — aborting request', request.url);
    return EMPTY;
  }

  const authRequest = addAuthorizationHeader(request, authData.token);

  const claims = decodeJwt<{ iat: number; exp: number }>(authData.token);
  if (claims) {
    const now = Date.now() / 1000;
    const buffer = (claims.exp - claims.iat) * 0.1;
    if (claims.exp - now < buffer) {
      authService.refreshToken().subscribe({
        error: err => {
          if (err.status === 401) authService.logout();
        }
      });
    }
  }

  return next(authRequest).pipe(
    catchError(error => {
      if (error.status !== 401) {
        return throwError(() => error);
      }
      return authService.refreshToken().pipe(
        catchError(err => {
          authService.logout();
          return throwError(() => err);
        }),
        switchMap(auth => next(addAuthorizationHeader(request, auth.token)))
      );
    })
  );
}
